import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getBachList,
  getPriceChangesHistory,
  getPriceChangesViewAll
} from "src/api/bi";
import { resetStore } from "src/store/actions";
import { formatToCurrency, getComponentKey } from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import { sorter } from "src/utils/sorter";
import {
  ViewAllDsdTableInitialState,
  ViewAllDsdTableRequestParams,
  FetchBatchListInterface
} from "../../../config/tableInterface/interface";

export const fetchViewAllDsdData = createAsyncThunk(
  "viewItems/fetchViewAllDsdData",
  (payload: ViewAllDsdTableRequestParams) => getPriceChangesViewAll(payload)
);

export const fetchBachList = createAsyncThunk(
  "viewItems/fetchBatchList",
  (payload: FetchBatchListInterface) => getBachList(payload)
);

export const fetchPriceChangeHistoryData = createAsyncThunk(
  "viewItems/fetchPriceChangeHistoryData",
  ({ itemId, storeId }: { itemId: string; storeId: string }) =>
    getPriceChangesHistory({ itemId, storeId })
);

const initialState: ViewAllDsdTableInitialState = {
  data: null,
  status: "idle",
  error: null,
  historyData: null,
  historyApiStatus: "idle",
  batchApiStatus: "idle",
  batchList: [],
  errorCount: 0
};

const priceChangesViewAllSlice = createSlice({
  name: "priceChangesViewAll",
  initialState,
  reducers: {
    sortItems(state: any, action: any) {
      const { sortParam, sortOrder } = action.payload;
      const items = [...state.historyData];
      state.historyData = getSortedPriceChangeItems(
        items,
        sortOrder,
        sortParam
      );
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchViewAllDsdData.pending, (state) => {
        state.status = "pending";
        state.errorCount += 1;
      })
      .addCase(fetchViewAllDsdData.fulfilled, (state, { payload }) => {
        state.errorCount -= 1;
        if (payload?.data?.length > 0) {
          state.data = payload.data.map((item: any) => {
            const currentRetail = formatToCurrency(
              stringToNumber(item.currentRetail)
            );
            const newRetail = formatToCurrency(stringToNumber(item.newRetail));
            return {
              ...item,
              item: item.item?.trim(),
              description: item.item?.trim(),
              batch: item.batchId,
              currentRetail,
              newRetail,
              priceChangesDepartment: item.department,
              key: getComponentKey()
            };
          });
        } else {
          state.data = [];
        }
        state.status = "idle";
      })
      .addCase(fetchViewAllDsdData.rejected, (state) => {
        state.status = state.errorCount === 1 ? "error" : "pending";
        state.data = [];
        state.errorCount -= 1;
      })
      .addCase(fetchPriceChangeHistoryData.pending, (state) => {
        state.historyApiStatus = "pending";
      })
      .addCase(fetchPriceChangeHistoryData.fulfilled, (state, { payload }) => {
        state.historyApiStatus = "idle";
        if (payload?.data?.length > 0) {
          state.historyData = getSortedPriceChangeItems(
            payload.data,
            "DESC",
            "date"
          );
        } else {
          state.historyData = null;
        }
      })
      .addCase(fetchPriceChangeHistoryData.rejected, (state) => {
        state.historyApiStatus = "error";
        state.historyData = null;
      })
      .addCase(fetchBachList.pending, (state) => {
        state.batchApiStatus = "pending";
      })
      .addCase(fetchBachList.fulfilled, (state, { payload }) => {
        state.batchApiStatus = "idle";
        if (payload?.data?.length > 0) {
          state.batchList = payload.data.map(
            (batch: { batchNum: string; date: string }) => ({
              id: batch.batchNum,
              name: batch.batchNum,
              date: batch.date
            })
          );
        }
      })
      .addCase(fetchBachList.rejected, (state) => {
        state.batchApiStatus = "error";
      });
  }
});

const getSortedPriceChangeItems = (
  items: any,
  sortOrder: string,
  sortParam: string
) => sorter(items, sortParam, sortOrder);

export const { sortItems } = priceChangesViewAllSlice.actions;

export const selectViewAllDsdTableData = ({
  ViewAllDsdTable
}: {
  ViewAllDsdTable: ViewAllDsdTableInitialState;
}) => ViewAllDsdTable.data;

export const selectViewAllDsdTableApiStatus = ({
  ViewAllDsdTable
}: {
  ViewAllDsdTable: ViewAllDsdTableInitialState;
}) => ViewAllDsdTable.status;

export const selectViewAllDsdTableError = ({
  ViewAllDsdTable
}: {
  ViewAllDsdTable: ViewAllDsdTableInitialState;
}) => ViewAllDsdTable.error;

export const selectPriceChangeHistoryData = ({
  priceChangesViewAll
}: {
  priceChangesViewAll: ViewAllDsdTableInitialState;
}) => priceChangesViewAll.historyData;

export const selectPriceChangeHistoryApiStatus = ({
  priceChangesViewAll
}: {
  priceChangesViewAll: ViewAllDsdTableInitialState;
}) => priceChangesViewAll.historyApiStatus;

export const selectPriceChangeBatchList = ({
  priceChangesViewAll
}: {
  priceChangesViewAll: ViewAllDsdTableInitialState;
}) => priceChangesViewAll.batchList;

export const selectPriceChangeBatchApiStatus = ({
  priceChangesViewAll
}: {
  priceChangesViewAll: ViewAllDsdTableInitialState;
}) => priceChangesViewAll.batchApiStatus;

export default priceChangesViewAllSlice.reducer;
